import type { Editor } from '@tiptap/react';
import { useCallback, useContext, useState } from 'react';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

import Icon from '../../components/Icon';
import IcoArrowDropDownRounded from 'components/icon/icons/IcoArrowDropDownRounded';
import PyMuiMenu from '../../components/generalComponents/PyMuiMenu';
import { PyMuiButton } from '../../components/generalComponents';
import PyMuiMenuLabel from '../../components/generalComponents/PyMuiMenuLabel';
import { AIGradientIcon } from './AIGradientIcon';
import { AIMenuContent } from './AIMenuContent';
import { AIContainer } from './styles';
import { COLORS } from '../../../../../../muiTheme/colors.ts';
import useFeatureFlag from 'hooks/useFeatureFlag.ts';
import { FeatureFlags } from 'utils/featureFlags.ts';
import { isTokenInvalid } from 'hooks/useTiptapAiToken.ts';
import { AITokenContext } from './AITokenProvider.tsx';

type AIProps = {
  editor: Editor | null;
  disabled?: boolean;
};

export const AI = ({ editor, disabled = false }: AIProps) => {
  const featureFlags = useFeatureFlag([FeatureFlags.aiCustomEditorPrompt]);
  const aiCustomEditorPromptEnabled = featureFlags[FeatureFlags.aiCustomEditorPrompt] ?? false;
  const aiContext = useContext(AITokenContext);
  const token = aiContext?.token;
  const isAIDisabled = disabled || !token || isTokenInvalid(token);

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [promptDialogOpen, setPromptDialogOpen] = useState(false);
  const [prompt, setPrompt] = useState('');

  const handleOpenMenu = useCallback<React.MouseEventHandler<HTMLElement>>((event) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = () => setAnchorEl(null);

  const handleFixGrammar = useCallback(() => {
    editor?.commands.fixGrammar();
    handleClose();
  }, [editor, handleClose]);

  const getHasTextSelected = useCallback((): boolean => {
    if (!editor) return false;
    return !editor.state.selection.empty;
  }, [editor]);

  const handleSimplifyText = useCallback(() => {
    editor?.commands.simplifyText();
    handleClose();
  }, [editor, handleClose]);

  const handleCompleteSentence = useCallback(() => {
    editor?.commands.completeSentence();
    handleClose();
  }, [editor, handleClose]);

  const handleOpenPromptDialog = () => setPromptDialogOpen(true);

  const handleClosePromptDialog = () => {
    setPromptDialogOpen(false);
    setPrompt('');
  };

  const handleGenerateWithPrompt = useCallback(() => {
    if (!editor || !prompt.trim()) return;

    const overwriteBlock = !getHasTextSelected();
    editor.commands.generateWithPrompt(prompt.trim(), overwriteBlock);

    handleClosePromptDialog();
    handleClose();
  }, [editor, prompt, getHasTextSelected, handleClosePromptDialog, handleClose]);

  return (
    <AIContainer>
      <PyMuiButton disabled={isAIDisabled} testId="ai-button" isMenuOpen={Boolean(anchorEl)} onClickAction={handleOpenMenu} width={72}>
        <AIGradientIcon
          icon={(id) => <AutoAwesomeIcon sx={{ fill: `${isAIDisabled ? COLORS.disabledTiptapIcon : `url(#${id})`}`, fontSize: '18px' }} />}
        />
        <PyMuiMenuLabel style={{ minWidth: 'auto' }}>AI</PyMuiMenuLabel>
        <Icon>
          <IcoArrowDropDownRounded />
        </Icon>
      </PyMuiButton>

      <PyMuiMenu
        testId="ai-menu"
        id="ai-features-menu"
        menuListProps="ai-features-button"
        anchorEl={anchorEl}
        isMenuOpen={Boolean(anchorEl)}
        handleClose={handleClose}
        sx={{
          '& .MuiPaper-root': {
            minWidth: '280px',
            width: { xs: '280px', lg: '365px' },
          },
          '& .MuiList-root': {
            width: { xs: '280px', lg: '365px' },
          },
        }}
      >
        <AIMenuContent
          promptDialogOpen={promptDialogOpen}
          prompt={prompt}
          onPromptChange={setPrompt}
          onClosePromptDialog={handleClosePromptDialog}
          onGenerateWithPrompt={handleGenerateWithPrompt}
          hasTextSelected={getHasTextSelected()}
          aiCustomEditorPromptEnabled={aiCustomEditorPromptEnabled}
          onFixGrammar={handleFixGrammar}
          onSimplifyText={handleSimplifyText}
          onCompleteSentence={handleCompleteSentence}
          onOpenPromptDialog={handleOpenPromptDialog}
        />
      </PyMuiMenu>
    </AIContainer>
  );
};
